import { useCallback, useState } from 'react';

import {
  useCart,
  useModal,
  useMyAccount,
  useMyStore,
  useNavigateTo,
  useProductEntryDataLayerValues,
  useTireWheelPackage,
  useTreadwellPreferences
} from '../hooks';

import { trackAction } from '../../utils/analytics/analytics';
import { clearLastPlpProduct } from '../../utils/back-to-results/back-to-results';
import { getNormalizedProductType } from '../../utils/product/product';
import { getTreadwellAnalyticsInfo } from '../../utils/treadwell/treadwell';
import { isArrayWithLength } from '../../utils/validators/validators';

import { MarkDownPriceType } from '../../types/graphql-types';
import {
  CART_PRODUCT_FLEET_PREFERRED,
  PRODUCT_PRICE_IS_FLEET,
  PRODUCT_STOCK_LEVEL_IS_FROM_MY_STORE
} from '../../constants/data-layer';
import {
  ADD_TO_CART_DRAWER,
  CONFIRM_STORE_DRAWER
} from '../../constants/modals';
import { BUNDLE_TYPE } from '../../constants/products';

function useAddProductToCart(args) {
  const { onProductAdded } = args || {};
  const [isAddingProduct, setIsAddingProduct] = useState(false);
  const { addProductSetToCart, addSetToCart, addToCart } = useCart();
  const navigateTo = useNavigateTo();
  const { isMyStoreConfirmed, myStore, setStore } = useMyStore();
  const myStoreCode = myStore?.code;
  const {
    addPrebuiltPackageToCart,
    addProductToTireWheelPackage,
    currentTireWheelPackageFlow,
    incompletePackage
  } = useTireWheelPackage();
  const { treadwellPreferences } = useTreadwellPreferences();
  const { getProductEntryDataLayerValues } = useProductEntryDataLayerValues();
  const { closeModal, openModal } = useModal();
  const { isTreadwellFlow } = useTreadwellPreferences();
  const { isFleetAccount } = useMyAccount();

  const isFleetEnabled =
    process.env.__FF_FLEET_ACCOUNT__ === 'true' && isFleetAccount;
  const isFleetTreadwell =
    process.env.__FF_FLEET_TREADWELL__ === 'true' && isFleetEnabled;

  const shouldAddToPackage = Boolean(currentTireWheelPackageFlow);

  const confirmedAddProduct = useCallback(
    async (myStoreProductItem, storeCodeArg) => {
      const {
        prebuiltPackage,
        product,
        productSet,
        productSetQuantity,
        quantity,
        rearQuantity
      } = myStoreProductItem || {};
      const {
        code: productCode,
        rearProduct,
        recommendationData
      } = product || {};
      let modalType;
      let modalProps;

      let storeCode;

      if (storeCodeArg) {
        storeCode = storeCodeArg;
      } else if (product?.stock?.storeCode) {
        storeCode = product?.stock?.storeCode;
      } else {
        storeCode = myStoreCode;
      }

      const isFinishingPackage =
        incompletePackage &&
        getNormalizedProductType(incompletePackage?.product?.productType) !==
          getNormalizedProductType(myStoreProductItem?.product?.productType);
      const isFleetPreferred =
        product?.isFleetPreferred || product?.rearProduct?.isFleetPreferred;

      const analyticsInfo = [
        ...getTreadwellAnalyticsInfo({
          isByo: shouldAddToPackage,
          isFleetTreadwell,
          isPrebuilt: Boolean(prebuiltPackage),
          recommendationData,
          treadwellPreferences
        }),
        {
          key: PRODUCT_STOCK_LEVEL_IS_FROM_MY_STORE,
          value: storeCode === myStoreCode ? '1' : '0'
        }
      ];

      if (process.env.__FF_FLEET_ACCOUNT__ === 'true') {
        analyticsInfo.push(
          {
            key: CART_PRODUCT_FLEET_PREFERRED,
            value: isFleetPreferred ? '1' : '0'
          },
          {
            key: PRODUCT_PRICE_IS_FLEET,
            value:
              product?.markdownPriceData?.markDownPriceType ===
              MarkDownPriceType.Fleet
                ? '1'
                : '0'
          }
        );
      }

      setIsAddingProduct(true);
      if (shouldAddToPackage) {
        // adding another product of the same type will overwrite the existing product
        if (isFinishingPackage) {
          modalType = ADD_TO_CART_DRAWER;
          modalProps = {
            entriesAdded: isFinishingPackage
              ? [incompletePackage, myStoreProductItem]
              : [myStoreProductItem],
            isPackage: true,
            quantity,
            rearQuantity,
            shouldPromptCancelOnClose: true
          };
        }

        await addProductToTireWheelPackage(
          analyticsInfo,
          product,
          quantity,
          rearProduct,
          rearQuantity,
          storeCode
        );
        trackAction(
          'shop_package_add_to_package',
          getProductEntryDataLayerValues(product)
        );
      } else if (prebuiltPackage) {
        modalType = ADD_TO_CART_DRAWER;

        modalProps = {
          entriesAdded: [
            { product: prebuiltPackage.product1, quantity: 4 * quantity },
            { product: prebuiltPackage.product2, quantity: 4 * quantity }
          ],
          hideStatus: true,
          isPackage: true,
          isPrebuilt: true
        };

        await addPrebuiltPackageToCart({
          analyticsInfo,
          prebuiltPackage,
          quantity,
          storeCode
        });
      } else {
        modalType = ADD_TO_CART_DRAWER;
        modalProps = {
          product,
          productSet,
          productSetQuantity,
          quantity,
          rearProduct,
          rearQuantity
        };

        const isRecommended = Boolean(recommendationData);

        if (product && quantity && rearProduct && rearQuantity) {
          await addSetToCart(storeCode, {
            analyticsInfo,
            dryStoppingDistance: recommendationData?.stopping?.dryDistance,
            frontProductCode: productCode,
            frontQuantity: quantity,
            isRecommended: isTreadwellFlow ? isRecommended : false,
            rearProductCode: rearProduct?.code,
            rearQuantity: rearQuantity,
            recommendationRank: recommendationData?.rank,
            wetStoppingDistance: recommendationData?.stopping?.wetDistance
          });
        } else if (productSet && productSetQuantity) {
          const productSetEntries = productSet?.map((product, idx) => ({
            analyticsInfo,
            fitmentPosition: product?.fitmentPosition?.code,
            productCode: product?.code,
            quantity: productSetQuantity?.[idx]
          }));
          await addProductSetToCart(storeCode, productSetEntries);
        } else {
          const isAddingRearProductOnly =
            rearProduct && rearQuantity && !quantity;
          const productCodeToAdd = isAddingRearProductOnly
            ? rearProduct?.code
            : productCode;
          const quantityToAdd = isAddingRearProductOnly
            ? rearQuantity
            : quantity;

          await addToCart(storeCode, {
            analyticsInfo,
            dryStoppingDistance: recommendationData?.stopping?.dryDistance,
            fitmentPosition: product?.fitmentPosition?.code,
            isRecommended: isTreadwellFlow ? isRecommended : false,
            productCode: productCodeToAdd,
            quantity: quantityToAdd,
            recommendationRank: recommendationData?.rank,
            wetStoppingDistance: recommendationData?.stopping?.wetDistance
          });
        }
      }

      if (onProductAdded) {
        onProductAdded();
      } else if (shouldAddToPackage && !isFinishingPackage) {
        clearLastPlpProduct();
        navigateTo('#/package/step-one-complete');
      } else {
        openModal(modalType, modalProps);
      }
      setIsAddingProduct(false);
    },
    [
      addPrebuiltPackageToCart,
      addProductSetToCart,
      addProductToTireWheelPackage,
      addSetToCart,
      addToCart,
      getProductEntryDataLayerValues,
      incompletePackage,
      isFleetTreadwell,
      isTreadwellFlow,
      myStoreCode,
      navigateTo,
      onProductAdded,
      openModal,
      shouldAddToPackage,
      treadwellPreferences
    ]
  );

  const promptStoreChangeConfirmation = useCallback(
    async (productItem, storeCode) => {
      const resp = await setStore(storeCode, {
        productItem,
        shouldConfirmStore: true
      });

      if (resp?.newProductData) {
        confirmedAddProduct(productItem, storeCode);
      }
    },
    [setStore, confirmedAddProduct]
  );

  const promptStoreConfirmation = useCallback(
    async productItem => {
      if (!myStore?.code) {
        return;
      }

      openModal(CONFIRM_STORE_DRAWER, {
        onClose: () => {
          closeModal();
        },
        onConfirmStore: result => {
          const myStoreProductItem = JSON.parse(JSON.stringify(productItem));
          const { bundleType, isSiteSwapping, newProductData, storeData } =
            result || {};
          const { code: storeCode } = storeData || {};

          if (newProductData) {
            if (bundleType === BUNDLE_TYPE.TIRE_WHEEL_PACKAGE) {
              myStoreProductItem.prebuiltPackage = newProductData;
            } else {
              myStoreProductItem.product = newProductData;
            }
          }

          if (storeCode && !isSiteSwapping) {
            confirmedAddProduct(myStoreProductItem, storeCode);
          }
          closeModal();
        },
        productItem: productItem
      });
    },
    [confirmedAddProduct, closeModal, myStore, openModal]
  );

  const addProduct = useCallback(
    ({
      isStoreConfirmed = isMyStoreConfirmed,
      productItem,
      shouldChangeStoreOverride,
      storeCode
    }) => {
      let productStoreCode = productItem?.product?.stock?.storeCode;

      if (productItem?.prebuiltPackage?.product1) {
        productStoreCode =
          productItem.prebuiltPackage.product1?.stock?.storeCode;
      } else if (isArrayWithLength(productItem?.productSet)) {
        productStoreCode = productItem?.productSet?.[0]?.stock?.storeCode;
      }

      const selectedStoreCode = storeCode || productStoreCode || myStore?.code;
      const shouldChangeStore =
        (!storeCode || shouldChangeStoreOverride) &&
        selectedStoreCode !== myStore?.code;

      if (shouldChangeStore) {
        promptStoreChangeConfirmation(productItem, selectedStoreCode);
      } else if (!isStoreConfirmed) {
        promptStoreConfirmation(productItem);
      } else {
        confirmedAddProduct(productItem, selectedStoreCode);
      }
    },
    [
      confirmedAddProduct,
      isMyStoreConfirmed,
      myStore,
      promptStoreConfirmation,
      promptStoreChangeConfirmation
    ]
  );

  return {
    addProduct,
    isAddingProduct
  };
}

export default useAddProductToCart;
